import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  customSurfaceTreatmentsQuery,
  getCustomSurfaceTreatmentsQueryData,
  setCustomSurfaceTreatmentsQueryData,
} from "#modules/companySettings/infra/customSurfaceTreatments.query";
import { updateCustomSurfaceTreatmentApi } from "#modules/companySettings/infra/updateCustomSurfaceTreatment.api";
import { processStepCatalogQuery } from "#modules/quoteRequestLines/infra/processStepCatalogQuery";

export const useUpdateCustomSurfaceTreatment = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateCustomSurfaceTreatmentApi,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: customSurfaceTreatmentsQuery(variables.companyId).queryKey,
      });
      const previous = getCustomSurfaceTreatmentsQueryData(queryClient, variables.companyId);

      if (previous) {
        setCustomSurfaceTreatmentsQueryData(
          queryClient,
          variables.companyId,
          previous.map((row) =>
            row.id === variables.surfaceTreatmentId ? { ...row, ...variables.payload } : row,
          ),
        );
      }

      return { previous, companyId: variables.companyId };
    },
    onSuccess: (data, _variables, context) => {
      if (!context.companyId) {
        return;
      }

      const current = getCustomSurfaceTreatmentsQueryData(queryClient, context.companyId);

      if (current) {
        setCustomSurfaceTreatmentsQueryData(
          queryClient,
          context.companyId,
          current.map((row) => (row.id === data.id ? data : row)),
        );
      }
    },
    onError: (err: Error, variables, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previous) {
        setCustomSurfaceTreatmentsQueryData(queryClient, variables.companyId, context.previous);
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries(customSurfaceTreatmentsQuery(variables.companyId));
      void queryClient.invalidateQueries(processStepCatalogQuery());
    },
  });

  return { updateCustomSurfaceTreatment: mutate, ...rest };
};
